/**
 * Live-score lock-screen notification (Android).
 *
 * The server sends a SILENT data-only push (channel 'match-live', no title/body)
 * on kickoff, each goal, and full-time. expo-notifications wakes the background
 * task defined here, which posts ONE sticky notification per match - re-posting
 * with the same `identifier` updates it in place (Android maps identifier -> the
 * native tag). So the lock screen shows a single live score that changes per
 * goal, then settles to the full-time result.
 *
 * Limits (Android, no foreground service): the notification is non-swipeable
 * while live but is best-effort when the app is force-killed; the minute only
 * refreshes on score changes, not every second.
 */
import * as Notifications from 'expo-notifications';
import * as TaskManager from 'expo-task-manager';
import { Platform } from 'react-native';

export const LIVE_SCORE_TASK = 'TASHKEELA_LIVE_SCORE_TASK';
const VOLT = '#22D3FF';

interface LivePayload {
  type?: 'live' | 'fulltime';
  fixtureId?: string;
  home?: string;
  away?: string;
  homeFlag?: string;
  awayFlag?: string;
  scoreHome?: number;
  scoreAway?: number;
  minute?: number | null;
  locale?: string;
}

/**
 * Dig the push data out of the platform-varying background task payload. Expo
 * sometimes nests it (`data.notification…`) or stringifies it under `body`, so
 * we walk the object for the first node carrying a `fixtureId`.
 */
function extractPayload(taskData: unknown): LivePayload | null {
  const seen = new Set<unknown>();
  const visit = (node: unknown): LivePayload | null => {
    if (!node || typeof node !== 'object' || seen.has(node)) return null;
    seen.add(node);
    const rec = node as Record<string, unknown>;
    if (typeof rec.fixtureId === 'string') return rec as LivePayload;
    if (typeof rec.body === 'string') {
      try {
        const parsed = JSON.parse(rec.body) as Record<string, unknown>;
        if (typeof parsed.fixtureId === 'string') return parsed as LivePayload;
      } catch {
        // body wasn't json; keep walking
      }
    }
    for (const key of ['data', 'notification', 'request', 'content', 'remoteMessage', 'payload']) {
      const child = visit(rec[key]);
      if (child) return child;
    }
    return null;
  };
  return visit(taskData);
}

async function postLiveNotification(p: LivePayload): Promise<void> {
  // CRITICAL guard: this background task runs for EVERY incoming push (Expo
  // registers it globally), and the real match-alerts pushes (kickoff / goal /
  // full-time) carry ONLY { fixtureId } — no team names or score. Without this
  // guard the score line collapses to a bare "0 - 0" and we post a useless
  // "0 - 0 · LIVE" sticky (the "empty notification" bug). Only post when the
  // payload is a genuine live-score push carrying both team names; the server no
  // longer sends such a push, so the sticky simply stays dormant rather than
  // spamming a blank card on top of the real alerts.
  if (!p.fixtureId || !p.home || !p.away) return;
  const ar = p.locale === 'ar';
  // "🇲🇦 Morocco 2 - 1 Brazil 🇧🇷" - flags + names come pre-localized in the push.
  // Wrap in a Left-to-Right Isolate (U+2066..U+2069) so Arabic bidi keeps the
  // flags beside their own team instead of jumbling the order.
  const score =
    '⁦' +
    [p.homeFlag, p.home, `${p.scoreHome ?? 0} - ${p.scoreAway ?? 0}`, p.away, p.awayFlag]
      .filter(Boolean)
      .join(' ') +
    '⁩';
  const identifier = `live-${p.fixtureId}`;
  const trigger = (Platform.OS === 'android'
    ? { channelId: 'match-live' }
    : null) as Notifications.NotificationTriggerInput;
  try {
    if (p.type === 'fulltime') {
      await Notifications.scheduleNotificationAsync({
        identifier,
        content: {
          title: ar ? 'انتهت المباراة' : 'Full time',
          body: score,
          color: VOLT,
          sticky: false,
          autoDismiss: true,
        },
        trigger,
      });
      return;
    }
    const liveLabel = ar ? 'مباشر' : 'LIVE';
    await Notifications.scheduleNotificationAsync({
      identifier,
      content: {
        title: score,
        body: p.minute != null ? `${liveLabel} · ${p.minute}'` : liveLabel,
        color: VOLT,
        sticky: true,
        autoDismiss: false,
      },
      trigger,
    });
  } catch {
    // Posting can fail (permission/channel not ready); never throw from the task.
  }
}

// Defined at module load so the headless background JS context can run it.
try {
  TaskManager.defineTask(LIVE_SCORE_TASK, async ({ data, error }) => {
    if (error) return;
    const payload = extractPayload(data);
    if (payload) await postLiveNotification(payload);
  });
} catch {
  // defineTask is idempotent per name; ignore a double registration on reload.
}

let initialized = false;

/** Register the live-score channel + background task + foreground handler. */
export async function initLiveNotifications(): Promise<void> {
  if (Platform.OS !== 'android' || initialized) return;
  initialized = true;
  Notifications.setNotificationHandler({
    handleNotification: async (notification) => {
      const content = notification.request.content;
      const data = (content.data ?? {}) as { type?: string };
      // The live-score sticky is driven by a SILENT data-only push (type
      // 'live'/'fulltime', no title/body) that only wakes the background task.
      // Never render it (or any other content-less push) as a blank banner -
      // that's the "empty Tashkeela notification" bug. Real alerts (goal, FT,
      // red card, ratings) always carry a title/body and still show.
      // Also suppress the on-device WS goal/red-card banners (live-goal /
      // live-red-card): they only ever fire while the user is watching the live
      // pitch, where the in-app GoalFlash already celebrates the moment and the
      // server push delivers the shade notification (with the scorer). Showing
      // them too was a duplicate goal notification.
      const silent =
        data.type === 'live' ||
        data.type === 'fulltime' ||
        data.type === 'live-goal' ||
        data.type === 'live-red-card' ||
        (!content.title && !content.body);
      if (silent) {
        return {
          shouldShowAlert: false,
          shouldShowBanner: false,
          shouldShowList: false,
          shouldPlaySound: false,
          shouldSetBadge: false,
        };
      }
      // shouldShowAlert is the legacy key; shouldShowBanner/List are SDK 53+.
      return {
        shouldShowAlert: true,
        shouldShowBanner: true,
        shouldShowList: true,
        shouldPlaySound: true,
        shouldSetBadge: false,
      };
    },
  });
  try {
    await Notifications.setNotificationChannelAsync('match-live', {
      name: 'Live score',
      importance: Notifications.AndroidImportance.MAX,
      lockscreenVisibility: Notifications.AndroidNotificationVisibility.PUBLIC,
      sound: 'default',
      lightColor: VOLT,
    });
    await Notifications.registerTaskAsync(LIVE_SCORE_TASK);
  } catch {
    // Non-fatal: live notifications simply stay off if registration fails.
  }
}
